import React, { memo, useRef, useState, useEffect } from "react";
import DropDown from "./DropDown/DropDown";
import { SearchBox } from "react-instantsearch";
import { fetchAutocompleteSuggestions } from "../../services/genAI_autocomplete";
import { classifyQuery } from "../../services/query-classification";
import { useDebouncedCallback } from "use-debounce";
import { useSelector, useDispatch } from "react-redux";
import { useAuthContext } from "@asgardeo/auth-react";
import { homeActions } from "../../store/store";
import { QUERY_LIST } from "./query_list";
import {standardizeCaseNumber} from "../../services/standardize-casenumber"
import { Search, X } from "lucide-react";

// --- Define the mapping ---
const CLASS_TO_TAB_MAP = {
  default: 'All',
  legal_question: 'All',
  party: 'Parties',
  case_number: 'Case Number',
};
const DEFAULT_TAB = 'All';

const SearchBar = memo(
  ({
    size,
    isDropdown = false,
    PlaceHolder,
    enableSuggestions,
    enableFiltering,
    enableSearchTracking,
    SearchAsYouType,
  }) => {
    const containerRef = useRef(null);
    const autocompleteAbortRef = useRef(null);
    const latestSuggestionQueryRef = useRef("");
    const [searchQuery, setSearchQuery] = useState("");
    const [Query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const searchType = sessionStorage.getItem("searchType");
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
    const currentPage = useSelector((state) => state.home.currentPage);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(
      sessionStorage.getItem("searchBarInteracted") === "true"
    );
    const dispatch = useDispatch();
    const { state, getAccessToken, refreshAccessToken } = useAuthContext();
    let handleSuggestion = false;

    const filterInitialSuggestions = (query) => {
      if (!enableFiltering) return [];

      const currentSearchType =
        sessionStorage.getItem("searchType") || DEFAULT_TAB;
      if (currentSearchType === "Case Number" || currentSearchType === "Parties") {
        return [];
      }

      const normalizedQuery = query.trim().toLowerCase();
      if (normalizedQuery.length < 2) return [];

      const queryWords = normalizedQuery.split(/\s+/);
      return QUERY_LIST.filter((suggestion) => {
        const suggestionWords = suggestion.toLowerCase().split(/\s+/);
        return suggestionWords.some((_, index) =>
          queryWords.every((word, queryIndex) =>
            suggestionWords[index + queryIndex]?.startsWith(word)
          )
        );
      }).slice(0, 5);
    };

    const suggestionSelection = (suggestion) => {
      const words = suggestion.trim().split(/\s+/);
      const queryWords = searchQuery.trim().toLowerCase().split(/\s+/);
      const normalizedWords = words.map((word) => word.toLowerCase());
      let matchIndex = normalizedWords.findIndex((_, index) =>
        queryWords.every((word, queryIndex) =>
          normalizedWords[index + queryIndex]?.startsWith(word)
        )
      );

      if (words.length <= 10) {
        return { value: suggestion, truncated: false };
      }

      if (matchIndex === -1) {
        matchIndex = normalizedWords.findIndex((word) =>
          word.includes(queryWords[0])
        );
      }

      if (matchIndex === -1) {
        return { value: words.slice(0, 8).join(" "), truncated: true };
      }

      const start = Math.max(0, matchIndex - 3);
      const end = Math.min(
        words.length,
        matchIndex + queryWords.length + 5
      );
      const excerpt = words.slice(start, end).join(" ");
      return { value: excerpt, truncated: end < words.length };
    };

    const mergeSuggestions = (localSuggestions, remoteSuggestions) => {
      const seen = new Set();
      return [...localSuggestions, ...remoteSuggestions]
        .filter((suggestion) => {
          const normalized = suggestion.trim().toLowerCase();
          if (!normalized || seen.has(normalized)) return false;
          seen.add(normalized);
          return true;
        })
        .slice(0, 5);
    };

    const handleInput = (event) => {
      const newQuery = event.target.value;
      setQuery(newQuery);

      sessionStorage.setItem('originalUserQuery', newQuery);

      if (!enableSuggestions) return;

      setSearchQuery(newQuery);
      setHighlightIndex(-1);
      latestSuggestionQueryRef.current = newQuery.trim();
      autocompleteAbortRef.current?.abort();

      const localSuggestions = filterInitialSuggestions(newQuery);
      setSuggestions(localSuggestions);

      if (newQuery.trim().length >= 2) {
        debouncedFetchSuggestions(newQuery, localSuggestions);
      } else {
        debouncedFetchSuggestions.cancel();
      }
    };

    const triggerSearchWithClassification = async (rawQuery) => {
      const finalQuery = rawQuery.trim();

      if (!finalQuery) { return; }

      sessionStorage.setItem("isEmpty", false);
      
      // Get the current tab
      const currentTab = sessionStorage.getItem('searchType') || DEFAULT_TAB;
      

      // --- Check for quoted search first ---
      if (finalQuery.startsWith('"') && finalQuery.endsWith('"')) {
        // If query is quoted, go directly to Exact Match regardless of current tab
        const targetTab = 'Exact Match';
        dispatch(homeActions.setSelectedOption(targetTab));
        sessionStorage.setItem('searchType', targetTab);
        sessionStorage.setItem("searchedQuery", finalQuery);

        // Track if needed
        if (enableSearchTracking) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "search",
            event_category: "engagement",
            search_term: finalQuery,
            search_type_classified: targetTab,
            value: 1,
          });
        }

        // Search with the quoted text as-is
        newSearch(finalQuery);
        return;
      }
      
      // --- Check if we should classify the query ---
      let targetTab = currentTab;
      let queryForSearch = finalQuery;
      
      // Only perform classification if currently in All tab
      if (currentTab === 'All' || !currentTab) {
        let predictedClass = 'default';
        try {
          predictedClass = await classifyQuery(finalQuery, {
            isAuthenticated: state.isAuthenticated,
            getAccessToken,
            refreshAccessToken,
          });
        } catch (e) {
          console.warn('[SearchBar] classifyQuery failed, using heuristic fallback', e);
        }

        // Heuristic fallback if classification doesn't return in time or returns default
        if (!predictedClass || predictedClass === 'default') {
          const lower = finalQuery.toLowerCase();
          const caseNumHint = /\b(\d{2,}[a-z]{0,2}[/\- ](?:\d{2}|19\d{2}|20\d{2}))\b/.test(lower) || /(slr|nlr)/.test(lower) || /sc\s*\//i.test(finalQuery);
          const partiesHint = /\bv\.?\b|\bvs\.?\b|\bversus\b/i.test(finalQuery);
          if (caseNumHint) predictedClass = 'case_number';
          else if (partiesHint) predictedClass = 'party';
        }

        targetTab = CLASS_TO_TAB_MAP[predictedClass] || DEFAULT_TAB;
        dispatch(homeActions.setSelectedOption(targetTab));
        sessionStorage.setItem('searchType', targetTab);

        // Persist decision for debugging
        try {
          sessionStorage.setItem(
            'lastClassificationDecision',
            JSON.stringify({ ts: Date.now(), input: finalQuery, predictedClass, targetTab })
          );
        } catch (_) {}
      }
    
      // *** Process the query based on current/target tab type ***

      if (targetTab === 'Case Number') {
        // First apply the standard case number logic (for SC cases)
        queryForSearch = standardizeCaseNumber(finalQuery);

        // Ensures ALL case numbers get hyphenated
        if (queryForSearch.includes(' ')) {
          queryForSearch = queryForSearch.replace(/ /g, '-');
        }
      } else if (targetTab === 'Exact Match') {
        // Ensure query is properly quoted for Exact Match
        if (!queryForSearch.startsWith('"') || !queryForSearch.endsWith('"')) {
          queryForSearch = `"${queryForSearch.replace(/"/g, '')}"`;
        }
      }
      

      // Store the processed query
      sessionStorage.setItem("searchedQuery", queryForSearch);

      // --- Tracking logic ---
      if (enableSearchTracking) {
        window.dataLayer = window.dataLayer || [];
        const standardizedTerm = (queryForSearch !== finalQuery) ? queryForSearch : undefined;
        window.dataLayer.push({
          event: "search",
          event_category: "engagement",
          search_term: finalQuery,
          search_term_standardized: standardizedTerm,
          search_type_classified: targetTab,
          value: 1,
        });
      }

      // --- Perform Search with the properly formatted query ---
      newSearch(queryForSearch);
    };

    const handleFormSubmit = (event) => {
      event.preventDefault();
      const inputElement = event.target.querySelector("input");
      const inputValue = inputElement.value;

      sessionStorage.setItem('originalUserQuery', inputValue);

      sessionStorage.setItem("isEmpty", false);
      sessionStorage.setItem("searchedQuery", inputValue);
      
      // Use the classification-based search
      triggerSearchWithClassification(inputValue);
    };

    const handleReset = () => {
      sessionStorage.setItem("isEmpty", true);
      sessionStorage.removeItem("searchedQuery");
      setQuery("");
      setSearchQuery("");
      setSuggestions([]);
      latestSuggestionQueryRef.current = "";
      debouncedFetchSuggestions.cancel();
      autocompleteAbortRef.current?.abort();
    };

    const debouncedFetchSuggestions = useDebouncedCallback(async (query, localSuggestions) => {
      if (!enableSuggestions) return;

      const normalizedQuery = query.trim();
      if (normalizedQuery.length < 2) return;

      const controller = new AbortController();
      autocompleteAbortRef.current = controller;
      const remoteSuggestions = await fetchAutocompleteSuggestions(
        normalizedQuery,
        { signal: controller.signal }
      );

      if (
        !controller.signal.aborted &&
        latestSuggestionQueryRef.current === normalizedQuery
      ) {
        setSuggestions(mergeSuggestions(localSuggestions, remoteSuggestions));
      }
    }, 300);

    const handleSuggestionClick = (suggestion) => {
      const selectedQuery = suggestionSelection(suggestion).value;

      sessionStorage.setItem('originalUserQuery', selectedQuery);

      sessionStorage.setItem("isEmpty", false);
      sessionStorage.setItem("searchedQuery", selectedQuery);
      
      // Use the classification-based search
      triggerSearchWithClassification(selectedQuery);
      
      if (selectedQuery && enableSearchTracking) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "autocomplete",
          event_category: "engagement",
          autocomplete_term: selectedQuery,
          value: 1,
        });
      }
    };

    const newSearch = (query) => {
      const currentUrl = new URL(window.location.href);
      const params = new URLSearchParams(currentUrl.search);
      // Check if we're on the dictionary page
      const isDictionaryPage = currentUrl.pathname.includes('/dictionary');
      if (isDictionaryPage) {
        params.set('dictionary[query]', query);
        const newUrl = `/dictionary?${params.toString()}`;
        window.location.href = newUrl;
      } else {
        params.set('paralegal[query]', query);
        const newUrl = `/search?${params.toString()}`;
        window.location.href = newUrl;
      }
    };

    const handleKeyDown = (event) => {
      if (suggestions.length === 0) return;

      if (event.key === "ArrowDown") {
        setHighlightIndex((prevIndex) =>
          prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0
        );
        event.preventDefault();
      } else if (event.key === "ArrowUp") {
        setHighlightIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1
        );
        event.preventDefault();
      } else if (event.key === "Enter") {
        if (
          highlightIndex >= 0 &&
          highlightIndex < suggestions.length
        ) {
          handleSuggestionClick(suggestions[highlightIndex]);
          event.preventDefault();
        }
        setSearchQuery("");
      }
    };

    const handleInputFocus = () => {
      setIsInputFocused(true);
      // Remember that user has interacted with the search bar
      if (!hasInteracted) {
        setHasInteracted(true);
        sessionStorage.setItem("searchBarInteracted", "true");
      }
    };

    const handleInputBlur = () => {
      if (searchQuery.trim() === "") {
        setIsInputFocused(false);
      }
    };

    useEffect(() => {
      return () => {
        debouncedFetchSuggestions.cancel();
        autocompleteAbortRef.current?.abort();
      };
    }, [debouncedFetchSuggestions]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target)
        ) {
          setSearchQuery("");
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    useEffect(() => {
      const handleResize = () => {
        setIsDesktop(window.innerWidth >= 1024);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
      <div
        ref={containerRef}
        className={`${
          size === "small"
            ? "justify-start max-w-[820px]"
            : "justify-center px-5 sm:px-10 max-w-[720px]"
        } items-center w-full`}
      >
        <div
          className={`relative z-[100] flex ${size === "small" ? "h-[42px]" : "h-[52px]"} items-center text-pl-text-primary bg-pl-bg-elevated border-[1.5px] border-pl-border rounded-pl-lg px-4 transition-all duration-150`}
        >
          {isDropdown && (
            <div className="flex pr-10 mr-10 border-r border-gray-400">
              <DropDown size="small" />
            </div>
          )}

          <SearchBox
            placeholder={PlaceHolder}
            searchAsYouType={SearchAsYouType}
            onSubmit={handleFormSubmit}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            classNames={{
              root: "flex-1 ",
              input:
                "bg-transparent ps-1 md:ps-2 py-2 pe-5 md:pe-9 w-full text-15 md:text-16 focus:outline-none flex-1 placeholder-pl-text-tertiary",
              form: "flex w-full relative",
              loadingIcon: "flex h-20 w-20 absolute right-16 top-2 md:top-2.5",
            }}
            resetIconComponent={({ classNames }) => (
              <div
                onClick={handleReset}
                className={`${classNames.resetIcon} absolute right-16 top-1.5 md:top-2`}
              >
                <X size={20} className="text-pl-text-secondary" strokeWidth={1.5} />
              </div>
            )}
            submitIconComponent={({ classNames }) => (
              <div className={`${classNames.submitIcon}`}>
                <div className="flex justify-center items-center me-2">
                  <div className="h-6 border-l border-pl-border-strong me-3"></div>
                  <Search
                    size={20}
                    strokeWidth={1.5}
                    className="text-pl-text-tertiary"
                  />
                </div>
              </div>
            )}
          />
          {searchQuery.trim() !== "" && suggestions.length > 0 && (
            <div
              className={`absolute z-[100] w-full left-0 px-1 ${
                size === "small" ? "top-[54px]" : "top-[54px] md:top-16"
              }`}
            >
              <div className="z-[100] text-14 text-pl-text-primary bg-pl-bg-elevated rounded-pl-lg shadow-pl-md border border-pl-border overflow-hidden w-full">
                {suggestions.map((suggestion, index) => {
                  const selection = suggestionSelection(suggestion);
                  return (
                    <div
                      key={index}
                      className={`w-full px-3 md:px-4 py-2 md:py-3 text-left cursor-pointer ${
                        index === highlightIndex
                          ? "bg-pl-bg-subtle"
                          : "hover:bg-pl-bg-subtle"
                      }`}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {selection.value}{selection.truncated ? " …" : ""}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default memo(SearchBar);
